import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Facebook,
  Chrome,
  PieChart,
  School,
  Users,
  TrendingUp,
  ChevronRight,
  FileText,
  LayoutDashboard,
  Eye,
  Layers
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
  adminOnly?: boolean;
}

const ADMIN_CLIENT_ID = 9;

const ReportsSidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Get client_id from localStorage
  const userStr = localStorage.getItem('crm_user');
  const user = userStr ? JSON.parse(userStr) : null;
  const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk || null;
  const isAdmin = clientId === ADMIN_CLIENT_ID || clientId === '9' || clientId === 9;

  // Reports navigation items
  const navItems: NavItem[] = [
    {
      name: 'Overview',
      path: 'overview',
      icon: <BarChart3 className="h-4 w-4" />,
      description: 'Source & Status Matrix'
    },
    {
      name: 'Facebook',
      path: 'facebook',
      icon: <Facebook className="h-4 w-4" />,
      description: 'Facebook campaign reports'
    },
    {
      name: 'Google',
      path: 'google',
      icon: <Chrome className="h-4 w-4" />,
      description: 'Google campaign reports'
    },
    {
      name: 'Charts',
      path: 'charts',
      icon: <PieChart className="h-4 w-4" />,
      description: 'Visual analytics'
    },
    // Admin-only items
    {
      name: 'School Summary',
      path: 'school-summary',
      icon: <School className="h-4 w-4" />,
      description: 'School-wise leads summary',
      adminOnly: true
    },
    {
      name: 'Client Reports',
      path: 'client-reports',
      icon: <Users className="h-4 w-4" />,
      description: 'Client insights & analytics',
      adminOnly: true
    },
    {
      name: 'FB Ads Reports',
      path: 'fb-ads',
      icon: <TrendingUp className="h-4 w-4" />,
      description: 'Facebook Ads performance',
      adminOnly: true
    },
    {
      name: 'Visibility Report',
      path: 'visibility-report',
      icon: <Eye className="h-4 w-4" />,
      description: 'School views in blogs',
      adminOnly: true
    },
    {
      name: 'Master Report',
      path: 'master-report',
      icon: <LayoutDashboard className="h-4 w-4" />,
      description: 'Combined leads & FB Ads data',
      adminOnly: true
    },
    {
      name: 'Ad Level Report',
      path: 'ad-level-report',
      icon: <Layers className="h-4 w-4" />,
      description: 'Campaign/AdSet/Ad status breakdown',
      adminOnly: true
    }
  ];

  // Filter items based on admin status
  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    if (location.pathname === '/reports' || location.pathname === '/reports/') {
      return itemPath === 'overview';
    }
    const currentPath = location.pathname.replace('/reports/', '');
    return currentPath === itemPath;
  };

  // Helper function to generate the full href path
  const getFullPath = (path: string) => {
    return `/reports/${path}`;
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Reports</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Analytics & Insights</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {filteredNavItems.map((item) => {
          const isActive = isPathActive(item.path);

          return (
            <Link
              key={item.path}
              to={getFullPath(item.path)}
              className={`
                relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                ${isActive
                  ? 'bg-gray-50 dark:bg-gray-800 text-brandColor dark:text-brandColor border-l-4 border-brandColor'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }
                ${!isCollapsed ? 'space-x-3' : 'justify-center'}
              `}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-brandColor' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                {item.icon}
              </div>

              {/* Content */}
              {!isCollapsed && (
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.name}</span>
                    {item.adminOnly && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-600 rounded">Admin</span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                  {item.name}
                </div>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default ReportsSidebar;
